import React, { useState, useEffect } from "react";
import Select from "react-select";
import PdfPreviewModal from "../PreviewPdfModal";
import generateLogsPDF from "./GenerateLogPdf";
// Helper functions for formatting
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getStatusColor = (status) => {
  const colors = {
    "In Progress": "bg-blue-100 text-blue-800",
    "Not Started": "bg-gray-100 text-gray-800",
    Interrupted: "bg-yellow-100 text-yellow-800",
    Completed: "bg-green-100 text-green-800",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
};

const getPriorityColor = (priority) => {
  const colors = {
    "Important and Urgent": "bg-red-100 text-red-800",
    "Important and not Urgent": "bg-orange-100 text-orange-800",
    "Not Important and Urgent": "bg-yellow-100 text-yellow-800",
    "Not Important and Not Urgent": "bg-gray-100 text-gray-800",
  };
  return colors[priority] || "bg-gray-100 text-gray-800";
};

const selectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: "inherit",
    borderColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: "0.5rem",
    minHeight: "38px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "inherit",
  }),
  input: (base) => ({
    ...base,
    color: "inherit",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#1f2937",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#374151" : "transparent",
    color: "#fff",
  }),
};

const LogsList = ({ logs = [], templates, logType, onDelete, onRowClick }) => {
  // Filter states
  const [documentType, setDocumentType] = useState(() => {
    return localStorage.getItem("logsList_documentType") || "log";
  });

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("logsList_viewMode") || "table";
  });
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedAssignee, setSelectedAssignee] = useState("all");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPreviewLog, setShowPreviewLog] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Sort state
  const [sortField, setSortField] = useState("createdAt");
  const [sortAsc, setSortAsc] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Year options
  const yearOptions = Array.from({ length: 6 }, (_, i) =>
    (new Date().getFullYear() - i).toString()
  );

  const yearOptionsFormatted = [
    { value: "all", label: "All Years" },
    ...yearOptions.map((y) => ({ value: y, label: y })),
  ];
  const logTitles = {
    "secretarial-records": "Secretarial Records",
    "exec-admin": "Executive & Administrative",
    "case-brief": "Case Brief",
    "process-server-logs": "Process Server & Messenger Logs",
  };

  const logTitle = logTitles[logType] || "";

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedStatus,
    selectedPriority,
    selectedAssignee,
    selectedYear,
    searchQuery,
    itemsPerPage,
  ]);

  useEffect(() => {
    localStorage.setItem("logsList_documentType", documentType);
  }, [documentType]);

  useEffect(() => {
    localStorage.setItem("logsList_viewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (
      logType !== "secretarial-records" &&
      logType !== "exec-admin" &&
      logType !== "case-brief"
    ) {
      setDocumentType("log");
      localStorage.setItem("logsList_documentType", "log");
    }
  }, [logType]);

  // Build filter options
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "Not Started", label: "Not Started" },
    { value: "In Progress", label: "In Progress" },
    { value: "Interrupted", label: "Interrupted" },
    { value: "Completed", label: "Completed" },
  ];

  const priorityOptions = [
    { value: "all", label: "All Priorities" },
    { value: "Important and Urgent", label: "Important and Urgent" },
    { value: "Important and not Urgent", label: "Important and not Urgent" },
    { value: "Not Important and Urgent", label: "Not Important and Urgent" },
    {
      value: "Not Important and Not Urgent",
      label: "Not Important and Not Urgent",
    },
  ];

  // Get unique assignees based on log type
  const getAssigneeField = () => {
    if (logType === "secretarial-records") return "secretary";
    if (logType === "process-server-logs") return "processedBy";
    return "assignee";
  };

  const assigneeField = getAssigneeField();
  const assigneeOptions = [
    { value: "all", label: "All Assignees" },
    ...[...new Set(logs.map((log) => log[assigneeField]).filter(Boolean))].map(
      (name) => ({
        value: name,
        label: name,
      })
    ),
  ];

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    const documentTypeMatch =
      logType === "secretarial-records" ||
      logType === "exec-admin" ||
      logType === "case-brief"
        ? log.recordType === documentType ||
          (!log.recordType && documentType === "log")
        : true;

    const logYear = log.createdAt ? log.createdAt.slice(0, 4) : "";
    const yearMatch = selectedYear === "all" || logYear === selectedYear;

    // When includeCompleted is true, show ONLY completed logs
    // When includeCompleted is false, exclude completed logs UNLESS specifically selected in status filter
    if (includeCompleted) {
      if (log.status !== "Completed") {
        return false;
      }
    } else {
      if (log.status === "Completed" && selectedStatus !== "Completed") {
        return false;
      }
    }

    const statusMatch =
      selectedStatus === "all" || log.status === selectedStatus;

    const priorityMatch =
      selectedPriority === "all" || log.priority === selectedPriority;

    const assigneeMatch =
      selectedAssignee === "all" || log[assigneeField] === selectedAssignee;

    const taskField = "task";
    const searchMatch =
      searchQuery === "" ||
      log[taskField]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log[assigneeField]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (logType === "process-server-logs" &&
        (log.agency?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          log.to?.name?.toLowerCase().includes(searchQuery.toLowerCase())));

    return (
      documentTypeMatch &&
      yearMatch &&
      statusMatch &&
      priorityMatch &&
      assigneeMatch &&
      searchMatch
    );
  });

  const handleStatusChange = (selected) => {
    setSelectedStatus(selected.value);
    // If user selects "Completed" from dropdown, turn on the toggle
    if (selected.value === "Completed") {
      setIncludeCompleted(true);
    } else {
      setIncludeCompleted(false);
    }
  };

  const handleToggleCompleted = (checked) => {
    setIncludeCompleted(checked);
    // If toggle is turned on, set status to "Completed"
    // If toggle is turned off and status was "Completed", reset to "all"
    if (checked) {
      setSelectedStatus("Completed");
    } else if (selectedStatus === "Completed") {
      setSelectedStatus("all");
    }
  };

  // Sort logs
  const sortedLogs = [...filteredLogs].sort((a, b) => {
    // If includeCompleted is true, prioritize completed items
    if (includeCompleted) {
      const aCompleted = a.status === "Completed";
      const bCompleted = b.status === "Completed";

      // If one is completed and the other isn't, completed comes first
      if (aCompleted && !bCompleted) return -1;
      if (!aCompleted && bCompleted) return 1;

      // If both are completed, sort by dateCompleted
      if (aCompleted && bCompleted) {
        const aDate = new Date(a.dateCompleted || 0);
        const bDate = new Date(b.dateCompleted || 0);
        return bDate - aDate; // Most recent completed first
      }
    }

    // For non-completed items or when includeCompleted is false, use regular sorting
    let aVal, bVal;

    if (sortField === "createdAt" || sortField === "dateCompleted") {
      aVal = new Date(a[sortField] || 0);
      bVal = new Date(b[sortField] || 0);
      return sortAsc ? aVal - bVal : bVal - aVal;
    }

    if (sortField === "status" || sortField === "priority") {
      aVal = a[sortField] || "";
      bVal = b[sortField] || "";
    } else {
      aVal = a[sortField] || "";
      bVal = b[sortField] || "";
    }

    const cmp = aVal.toString().localeCompare(bVal.toString());
    return sortAsc ? cmp : -cmp;
  });

  // Pagination
  const totalItems = sortedLogs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = sortedLogs.slice(startIndex, endIndex);

  const clearFilters = () => {
    setSelectedStatus("all");
    setSelectedPriority("all");
    setSelectedAssignee("all");
    setSelectedYear(new Date().getFullYear().toString());
    setSearchQuery("");
    setIncludeCompleted(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const formatPriority = (priority) => {
    const abbreviations = {
      "Important and Urgent": "I&U",
      "Important and not Urgent": "I&NU",
      "Not Important and Urgent": "NI&U",
      "Not Important and Not Urgent": "NI&NU",
    };
    return abbreviations[priority] || priority;
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <span className="text-alt-color ml-1">↕</span>;
    }
    return <span className="text-white ml-1">{sortAsc ? "↑" : "↓"}</span>;
  };

  // Pagination Controls
  const PaginationControls = () => (
    <div className="flex items-center flex-col sm:flex-row sm:gap-0 gap-4 justify-between px-6 py-3 bg-none border-t dark:border-gray-600 border-gray-200">
      <div className="flex items-center space-x-2">
        <span className="text-sm text-alt-color">Show</span>
        <select
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-inherit focus:outline-none focus:ring-2 focus:ring-tier-secondary"
        >
          <option value={6}>6</option>
          <option value={12}>12</option>
          <option value={24}>24</option>
          <option value={48}>48</option>
        </select>
        <span className="text-xs sm:text-sm text-alt-color">
          entries | Showing {startIndex + 1} to {Math.min(endIndex, totalItems)}{" "}
          of {totalItems}
        </span>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">first_page</span>
        </button>
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">
            chevron_left
          </span>
        </button>

        <div className="flex items-center space-x-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 text-sm rounded ${
                  currentPage === pageNum
                    ? "bg-tier-secondary text-white"
                    : "text-tier-primary bg-white border border-gray-300 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">
            chevron_right
          </span>
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">last_page</span>
        </button>
      </div>
    </div>
  );

  // Card View
  const CardView = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
        {paginatedLogs.map((log) => (
          <div
            key={log.id}
            className="custom-card"
            onClick={() => onRowClick && onRowClick(log)}
          >
            <div className="p-4">
              <div className="flex justify-between items-start mb-3">
                {documentType === "nte" ? (
                  <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    Notice To Explain
                  </span>
                ) : (
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      log.status
                    )}`}
                  >
                    {log.status || "Not Started"}
                  </span>
                )}
                <div className="text-xs text-alt-color">
                  {formatDate(log.createdAt)}
                </div>
              </div>

              {documentType === "nte" ? (
                <>
                  <h3 className="font-semibold text-color mb-2 line-clamp-2">
                    {log.to?.name || "No Recipient"}
                  </h3>

                  <div className="space-y-1 text-sm text-alt-color mb-4">
                    <div className="flex items-center">
                      <span className="font-medium w-28">Date:</span>
                      <span>{formatDate(log.nteDate)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-color mb-2 line-clamp-2">
                    {log.task}
                  </h3>

                  <div className="space-y-1 text-sm text-alt-color mb-4">
                    <div className="flex items-center">
                      <span className="font-medium w-36">
                        {logType === "secretarial-records"
                          ? "Assigned Secretary:"
                          : "Assigned Server:"}
                      </span>
                      <span className="truncate">
                        {log[assigneeField] || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium w-20">Priority:</span>
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs ${getPriorityColor(
                          log.priority
                        )}`}
                      >
                        {log.priority}
                      </span>
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  className="action-btn-tb action-btn-edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePdf(log, "preview");
                  }}
                  title="View"
                >
                  <span className="material-symbols-outlined text-sm">
                    visibility
                  </span>
                </button>

                <button
                  className="action-btn-tb action-btn-download"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePdf(log, "download");
                  }}
                  title="Download"
                >
                  <span className="material-symbols-outlined text-sm">
                    download
                  </span>
                </button>

                <button
                  className="action-btn-tb action-btn-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete && onDelete(log.id);
                  }}
                  title="Delete"
                >
                  <span className="material-symbols-outlined text-sm">
                    delete
                  </span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {totalItems > 0 && (
        <div className="mt-4 bg-white/10 rounded-lg shadow-sm border dark:border-gray-600 border-alt-color overflow-hidden">
          <PaginationControls />
        </div>
      )}
    </>
  );

  // Table View
  const TableView = () => (
    <div className="mt-2 bg-white/10 rounded-lg shadow-sm border dark:border-gray-600 border-alt-color overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-alt-color dark:divide-gray-600">
          <thead className="bg-tier-secondary">
            <tr>
              {/* For process-server-logs, show Agency first */}
              {logType === "process-server-logs" ? (
                <th
                  className={`th-custom ${
                    sortField === "agency"
                      ? "text-white bg-dark-alt hover:bg-tier-secondary"
                      : ""
                  }`}
                  onClick={() => handleSort("agency")}
                >
                  <div className="flex items-center">
                    Agency
                    {getSortIcon("agency")}
                  </div>
                </th>
              ) : (
                <th
                  className={`th-custom ${
                    sortField === "createdAt"
                      ? "text-white bg-dark-alt hover:bg-tier-secondary"
                      : ""
                  }`}
                  onClick={() => handleSort("createdAt")}
                >
                  <div className="flex items-center">
                    Date Created
                    {getSortIcon("createdAt")}
                  </div>
                </th>
              )}

              {/* Conditional column headers based on document type */}
              {documentType === "nte" ? (
                <>
                  <th
                    className={`th-custom ${
                      sortField === "to.name"
                        ? "text-white bg-dark-alt hover:bg-tier-secondary"
                        : ""
                    }`}
                    onClick={() => handleSort("to.name")}
                  >
                    <div className="flex items-center">
                      Recipient
                      {getSortIcon("to.name")}
                    </div>
                  </th>
                  <th
                    className={`th-custom ${
                      sortField === "nteDate"
                        ? "text-white bg-dark-alt hover:bg-tier-secondary"
                        : ""
                    }`}
                    onClick={() => handleSort("nteDate")}
                  >
                    <div className="flex items-center">
                      Notice Date
                      {getSortIcon("nteDate")}
                    </div>
                  </th>
                </>
              ) : (
                <>
                  <th
                    className={`th-custom ${
                      sortField === "task"
                        ? "text-white bg-dark-alt hover:bg-tier-secondary"
                        : ""
                    }`}
                    onClick={() => handleSort("task")}
                  >
                    <div className="flex items-center">
                      Task
                      {getSortIcon("task")}
                    </div>
                  </th>

                  {/* Show Client header for process-server-logs */}
                  {logType === "process-server-logs" && (
                    <th
                      className={`th-custom ${
                        sortField === "client"
                          ? "text-white bg-dark-alt hover:bg-tier-secondary"
                          : ""
                      }`}
                      onClick={() => handleSort("client")}
                    >
                      <div className="flex items-center">
                        Client
                        {getSortIcon("client")}
                      </div>
                    </th>
                  )}

                  <th
                    className={`th-custom ${
                      sortField === "status"
                        ? "text-white bg-dark-alt hover:bg-tier-secondary"
                        : ""
                    }`}
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon("status")}
                    </div>
                  </th>

                  {/* Show Assigned Secretary for secretarial-records only */}
                  {logType === "secretarial-records" && (
                    <th
                      className={`th-custom ${
                        sortField === assigneeField
                          ? "text-white bg-dark-alt hover:bg-tier-secondary"
                          : ""
                      }`}
                      onClick={() => handleSort(assigneeField)}
                    >
                      <div className="flex items-center">
                        Assigned Secretary
                        {getSortIcon(assigneeField)}
                      </div>
                    </th>
                  )}

                  <th className="px-6 py-2 text-left text-xs font-medium text-tier-primary uppercase tracking-wider">
                    Priority
                  </th>
                </>
              )}

              <th className="px-6 py-2 text-right text-xs font-medium text-tier-primary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white/5 divide-y divide-alt-color dark:divide-gray-600">
            {paginatedLogs.length > 0 ? (
              paginatedLogs.map((log, index) => (
                <tr
                  key={log.id}
                  className={`cursor-pointer hover:bg-tier-secondary/10 dark:hover:bg-dark-alt/10 transition-colors ${
                    index % 2 === 0
                      ? "dark:bg-tier-primary/60"
                      : "bg-tier-primary dark:bg-tier-primary-dark"
                  }`}
                  onClick={() => onRowClick && onRowClick(log)}
                >
                  {/* Agency/Date Created column */}
                  {logType === "process-server-logs" ? (
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-alt-color">
                      {log?.agency || ""}
                    </td>
                  ) : (
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-alt-color">
                      {formatDate(log.createdAt)}
                    </td>
                  )}

                  {/* Conditional row data based on document type */}
                  {documentType === "nte" ? (
                    <>
                      <td className="px-6 py-3">
                        <div className="text-sm text-color max-w-xs truncate">
                          {log.to?.name || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-alt-color">
                        {formatDate(log.nteDate)}
                      </td>
                    </>
                  ) : (
                    <>
                      {/* Task column */}
                      <td className="px-6 py-3">
                        <div className="text-sm text-color max-w-xs truncate">
                          {log.task}
                        </div>
                      </td>

                      {/* Client column for process-server-logs */}
                      {logType === "process-server-logs" && (
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-color">
                          {log?.to?.name || "N/A"}
                        </td>
                      )}

                      {/* Status column */}
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            log.status
                          )}`}
                        >
                          {log.status || "Not Started"}
                        </span>
                      </td>

                      {/* Assigned Secretary for secretarial-records only */}
                      {logType === "secretarial-records" && (
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-color">
                          {log[assigneeField] || "N/A"}
                        </td>
                      )}

                      {/* Priority column */}
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                            log.priority
                          )}`}
                        >
                          {formatPriority(log.priority)}
                        </span>
                      </td>
                    </>
                  )}

                  <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        className="action-btn-tb action-btn-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePdf(log, "preview");
                        }}
                        title="View"
                      >
                        <span className="material-symbols-outlined text-sm">
                          visibility
                        </span>
                      </button>

                      <button
                        className="action-btn-tb action-btn-download"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePdf(log, "download");
                        }}
                        title="Download"
                      >
                        <span className="material-symbols-outlined text-sm">
                          download
                        </span>
                      </button>

                      <button
                        className="action-btn-tb action-btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete && onDelete(log.id);
                        }}
                        title="Delete"
                      >
                        <span className="material-symbols-outlined text-sm">
                          delete
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={
                    documentType === "nte" &&
                    (logType === "secretarial-records" ||
                      logType === "exec-admin")
                      ? 4
                      : logType === "exec-admin" || logType === "case-brief"
                      ? 5
                      : 6
                  }
                  className="px-6 py-12 text-center text-gray-500"
                >
                  <div className="flex flex-col items-center">
                    <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                      {documentType === "nte" ? "warning" : "description"}
                    </span>
                    <p className="text-sm">
                      No {documentType === "nte" ? "documents" : "logs"} found.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Try adjusting your filters or create a new{" "}
                      {documentType === "nte" ? "document" : "log"}.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalItems > 0 && <PaginationControls />}
    </div>
  );

  const handlePdf = async (log, action) => {
    try {
      const matchedTemplate = templates.find((t) => t.id === log.templateRef);

      const logTypeList = {
        "secretarial-records": "secretary",
        "exec-admin": "exec",
        "process-server-logs": "server",
        "case-brief": "casebrief",
      };
      const curLogType = logTypeList[logType];
      const pdfUrl = await generateLogsPDF(
        log,
        matchedTemplate,
        action,
        curLogType
      );

      if (action === "preview") {
        setPdfUrl(pdfUrl);
        setShowPreviewLog(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const closePreview = () => {
    setShowPreviewLog(false);
  };

  return (
    <div className="px-2 py-4 pt-6 max-w-6xl w-full mx-auto">
      {/* Header */}
      <div className="flex flex-col items-start text-color">
        <div className="flex w-full items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-color">
            {logTitle}
          </h2>
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* View Toggle */}
            <div className="flex items-center bg-inherit border border-gray-400 dark:border-gray-600 rounded-lg p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "table"
                    ? "bg-white/10 text-color shadow-sm"
                    : "text-alt-color hover:text-color"
                }`}
              >
                <span className="material-symbols-outlined text-sm mr-1">
                  table_view
                </span>
                <span className="sm:inline hidden">Table</span>
              </button>
              <button
                onClick={() => setViewMode("cards")}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "cards"
                    ? "bg-white/10 text-color shadow-sm"
                    : "text-alt-color hover:text-color"
                }`}
              >
                <span className="material-symbols-outlined text-sm mr-1">
                  grid_view
                </span>
                <span className="sm:inline hidden">Cards</span>
              </button>
            </div>

            {/* Count Badge */}
            <div className="flex items-center px-3 py-1 bg-amber-200/20 text-dark-alt rounded-full text-xs sm:text-sm font-medium">
              {sortedLogs.length} log{sortedLogs.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="w-full bg-white/5 rounded-lg shadow-sm border dark:border-gray-600 border-gray-200 p-4 mb-4">
          <div className="flex flex-col">
            <div className="flex flex-col md:flex-row justify-between">
              {/* Search */}
              <div className="md:w-1/2 w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search tasks..."
                  className="custom-input mb-2"
                />
              </div>
              <div className="flex justify-center flex-row items-center gap-2">
                {/* Document Type Toggle - only show for secretarial-records */}
                {(logType === "secretarial-records" ||
                  logType === "exec-admin" ||
                  logType === "case-brief") && (
                  <button
                    onClick={() =>
                      setDocumentType(documentType === "log" ? "nte" : "log")
                    }
                    className="btn-action-white"
                  >
                    <span>
                      {documentType === "log" ? "Letters" : "Logs & Records"}
                    </span>
                  </button>
                )}
                {/* Completed Checkbox */}
                <div className="flex flex-col items-center">
                  <label className="block text-sm font-medium text-alt-color mb-1">
                    Show Completed
                  </label>

                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeCompleted}
                      onChange={(e) => handleToggleCompleted(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-tier-secondary/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-tier-secondary"></div>
                  </label>
                </div>
              </div>
            </div>

            {documentType === "log" && (
              <>
                {/* Hidden when Letters are shown */}
                <div className="flex flex-wrap gap-4 items-end">
                  {/* Status Filter */}
                  <div className="flex-1 min-w-[150px]">
                    <label className="block text-sm font-medium text-alt-color mb-1">
                      Status
                    </label>
                    <Select
                      value={statusOptions.find(
                        (opt) => opt.value === selectedStatus
                      )}
                      onChange={handleStatusChange}
                      options={statusOptions}
                      styles={selectStyles}
                      isSearchable={false}
                      classNamePrefix="react-select"
                    />
                  </div>

                  {/* Priority Filter */}
                  <div className="flex-1 min-w-[150px]">
                    <label className="block text-sm font-medium text-alt-color mb-1">
                      Priority
                    </label>
                    <Select
                      value={priorityOptions.find(
                        (opt) => opt.value === selectedPriority
                      )}
                      onChange={(selected) =>
                        setSelectedPriority(selected.value)
                      }
                      options={priorityOptions}
                      styles={selectStyles}
                      classNamePrefix="react-select"
                      isSearchable={false}
                    />
                  </div>

                  {logType !== "nte" && logType !== "process-server-logs" && (
                    <>
                      {/* Assignee Filter */}
                      <div className="flex-1 min-w-[150px]">
                        <label className="block text-sm font-medium text-alt-color mb-1">
                          {logType === "secretarial-records"
                            ? "Assigned Secretary"
                            : "Assigned Server"}
                        </label>
                        <Select
                          value={assigneeOptions.find(
                            (opt) => opt.value === selectedAssignee
                          )}
                          onChange={(selected) =>
                            setSelectedAssignee(selected.value)
                          }
                          options={assigneeOptions}
                          styles={selectStyles}
                          classNamePrefix="react-select"
                          isSearchable
                        />
                      </div>
                    </>
                  )}

                  {/* Year Filter */}
                  <div className="flex-1 min-w-[120px]">
                    <label className="block text-sm font-medium text-alt-color mb-1">
                      Year
                    </label>
                    <Select
                      value={yearOptionsFormatted.find(
                        (opt) => opt.value === selectedYear
                      )}
                      onChange={(selected) => setSelectedYear(selected.value)}
                      options={yearOptionsFormatted}
                      styles={selectStyles}
                      classNamePrefix="react-select"
                      isSearchable={false}
                    />
                  </div>

                  {/* Clear Button */}
                  <button onClick={clearFilters} className="btn-action-white">
                    Clear Filters
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === "table" ? <TableView /> : <CardView />}

      {showPreviewLog && (
        <PdfPreviewModal
          invoiceData={null}
          pdfUrl={pdfUrl}
          onClose={closePreview}
        />
      )}
    </div>
  );
};

export default LogsList;
